import React, { useMemo, useState } from 'react'
import clsx from 'classnames'
import { Link } from 'react-router'
import { useSelector } from 'react-redux'
import type { Salon, SalonOptions } from 'api/api.types'
import { getActiveSalons, getSalons } from 'src/store/data.store'
import { formatPhone } from 'utils/formatPhone'
import { chunk } from 'utils/chunk'
import styles from './SalonsList.module.scss'

const OPTION_ICONS: Array<{ icon: string, title: string, option: keyof SalonOptions }> = [
  { icon: 'kids', title: 'Детский уголок', option: 'hasKids' },
  { icon: 'coffee', title: 'Чай / Кофе', option: 'hasCoffee' },
  { icon: 'ramp', title: 'Пандус для инвалидов', option: 'hasRamp' },
  { icon: 'parking', title: 'Городская парковка', option: 'hasParking' },
  { icon: 'wifi', title: 'Бесплатный WiFi', option: 'hasWifi' },
]

type SalonsListItemProps = {
  salon: Salon,
  onSelect?: (salonId: number) => void,
}

const SalonsListItem: React.FC<SalonsListItemProps> = ({ salon, onSelect }) => {
  const vars = salon.meta?.templateVars?.pageVars
  const phone = vars?.salonPhones?.[0]?.phone ?? ''
  const options = vars?.options

  return <div className={styles.salonContainer}>
    <span className={styles.salonName}>{vars?.salonName}</span>
    <span className={clsx([styles.salonText, styles.salonAddress])}>{vars?.salonAddress}</span>
    <span className={clsx([styles.salonText, styles.salonContacts])}>
      <a href={'tel:+' + phone} className={styles.salonPhone}>{formatPhone(phone)}</a>
      <span> | </span>
      <span className={styles.salonBusinessHours}>Ежедневно {salon.businessHours}</span>
    </span>
    <div className={styles.salonTextLinkContainer}>
      <div className={styles.salonTextLinkFix}>
        <Link to={salon.meta?.fullPath ?? '/salons'} className={styles.salonTextLink}>Подробнее</Link>
        <Link onClick={() => onSelect?.(salon.id)} className={styles.salonTextLink} to={'#appointment'}>
          Выбрать
        </Link>
      </div>
      <div className={styles.salonOptions}>
        {OPTION_ICONS.map(({ icon, title, option }) => (
          <i
            key={icon}
            data-icon={icon}
            title={title}
            className={clsx([styles.salonOption, { [styles.salonOptionActive]: options?.[option] }])}
          />
        ))}
      </div>
    </div>
  </div>
}

type SalonsListProps = {
  perPage?: number,
  onSelectSalon?: (salonId: number) => void,
  /** With controls: only salons of the selected city, a search box and pages; without: all salons */
  controls?: boolean,
}

export const SalonsList: React.FC<SalonsListProps> = (props) => {
  const { perPage = 8, onSelectSalon, controls = true } = props
  const salons = useSelector(controls ? getActiveSalons : getSalons)
  const [inputValue, setInputValue] = useState('')
  const [filterValue, setFilterValue] = useState('')
  const [page, setPage] = useState(0)

  const searchSalon = (e: React.FormEvent) => {
    e.preventDefault()
    setFilterValue(inputValue)
    setPage(0)
  }

  const salonsPages = useMemo(() => {
    if (!controls) {
      return [salons]
    }
    const lowerFilterValue = filterValue.toLowerCase()
    const filtered = salons.filter((s) => {
      const vars = s.meta?.templateVars?.pageVars
      return vars?.salonName?.toLowerCase()?.includes(lowerFilterValue)
        || vars?.salonAddress?.toLowerCase()?.includes(lowerFilterValue)
    })
    return chunk(filtered, perPage)
  }, [salons, filterValue, controls, perPage])

  return (
    <div className={styles.container}>
      {controls && (
        <div className={styles.salonSearch}>
          <form className={styles.form}>
            <input
              id="search"
              type="text"
              className={styles.salonForm}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
            <button type="submit" className={styles.salonFormBtn} onClick={searchSalon}>
              Найти
            </button>
          </form>
        </div>
      )}
      <div className={styles.salons}>
        {salonsPages[page]?.map((salon) => (
          <SalonsListItem key={salon.id} salon={salon} onSelect={onSelectSalon} />
        ))}
        {salonsPages.length === 0 && (
          <div>Салоны не найдены</div>
        )}
      </div>
      {salonsPages.length > 1 && <div className={styles.paginator}>
        {salonsPages.map((_, index) => (
          <div className={styles.paginatorContainer} key={index}>
            <span
              className={page === index ? styles.paginatorActivePage : styles.paginatorText}
              onClick={() => setPage(index)}
            >
              {index + 1}
            </span>
          </div>
        ))}
      </div>}
    </div>
  )
}
